/* eslint-disable no-use-before-define, @typescript-eslint/no-use-before-define, no-param-reassign, no-plusplus, no-nested-ternary, no-mixed-operators, no-void, no-continue, prefer-destructuring, no-restricted-syntax, no-cond-assign */
// Multiconexão, the five-route instrument — a port of the approved component's script (it ran inside an
// <iframe srcdoc> before the design went native). The section renders the resting markup; this module
// hydrates it in place: routes placed for the visitor's location, live pings every 0.5 s (2–5 ms on the
// winning route, 180–250 ms on the direct line), handovers every ~10 s, packets travelling the wires
// (Web Animations), hover/focus/pin on a route, "Testar rotas", pause.
//
// The lint rules switched off above are the ones a line-for-line port of an imperative animation engine
// trips on (functions hoisted across the whole closure, counters, in-place mutation of the route
// records); keeping the source's shape is what makes it reviewable against the approved original.
//
// Contract with the React side:
//   * every element is looked up under `root` — nothing outside the component is touched, except the
//     `np:mc-place` event on window (+ `window.__npMcPlace`) that the "3 vitórias" card mirrors;
//   * every runtime sentence comes from `strings`;
//   * the returned function tears everything down (timers, frames, observers, animations, listeners).
import { askVisitorGeo, forcedPlace, type GeoCity, type GeoData, normalizePlace, type VisitorPlace } from './geo';
import type { McStrings } from './strings';

export interface McCity {
  city: string;
  code: string;
  lat: number;
  lon: number;
  cc: string;
}

export interface McScenario {
  [routeId: string]: number;
  bad: number;
}

export interface McPlace {
  key: string;
  cc: string;
  hub: McCity;
  user: McCity | null;
  routes: McCity[];
  scenarios: McScenario[];
}

/** window.__npMcPlace: the page-level mirror of the current place (read by the "3 vitórias" card). */
export const mcPlaceHost = () => window as unknown as { __npMcPlace?: McPlace };

interface Box {
  l: number;
  t: number;
  r: number;
  b: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

interface Packet {
  el: HTMLDivElement;
  phase: number;
  anim: Animation | null;
}

interface MotionGeo {
  head: string;
  rest: string;
  w: number;
  parks: number[];
}

interface Mover {
  set: HTMLDivElement | null;
  packets: Packet[];
  geo: MotionGeo | null;
  frames: Keyframe[] | null;
  framesHide?: Keyframe[];
  framesOpen?: Keyframe[];
  parkIdx?: number[];
}

interface Route extends Mover {
  id: string;
  city: string;
  country: string;
  code: string;
  lat: number;
  shown: number;
  hist: number[];
  tw: number;
  el: HTMLButtonElement;
  msEl: HTMLElement;
  msWrap: HTMLElement;
  deltaEl: HTMLElement;
  spLine: SVGPolylineElement;
  spFill: SVGPolygonElement;
  spDot: SVGCircleElement;
  rect?: Box;
  g?: SVGGElement;
  pathBase?: SVGPathElement;
  pathGlow?: SVGPathElement;
  pathHot?: SVGPathElement;
  pathSweep?: SVGPathElement;
  pathKnock?: SVGPathElement;
  portIn?: SVGCircleElement;
  portOut?: SVGCircleElement;
}

interface Direct extends Mover {
  lat: number;
}

interface WinnerResult {
  best: Route;
  prev: Route | null;
  changed: boolean;
}

interface Geometry {
  mobile: boolean;
  dPc: number;
  dGame: number;
  a: { x: number; y: number };
  b: { x: number; y: number };
  ra: { x: number; y: number };
  rb: { x: number; y: number };
  routes: { base: string; full: string; l: number; r: number; cy: number; geo: MotionGeo }[];
  direct: { wire: string; head: string; tail: string; l: number; r: number; cy: number; geo: MotionGeo };
  trunks: [string, string];
}

export interface McOptions {
  root: HTMLElement;
  geo: GeoData;
  strings: McStrings;
  /** localized country name for an ISO code; falls back to the table's (pt) name */
  countryName: (code: string, fallback: string) => string;
}

const NS = 'http://www.w3.org/2000/svg';
const FALLBACK_GEO: VisitorPlace = { country: 'US', region: '', city: '', lat: null, lon: null };
const LAT_MIN = 2;
const BAD_LO = 180;
const BAD_HI = 250;
const HIST_N = 8;
const BASE_MS = 1600;
const SP_W = 52;
const SP_H = 18;
const SP_LO = 0;
const SP_HI = 40;
const SP_K = 2.2;
const MASK_PAD = 48;
const STUB_V = 14;
const LIVE_MS = 500;

function fold(text: string | undefined | null) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
function rint(low: number, high: number) {
  return low + Math.floor(Math.random() * (high - low + 1));
}
function km(from: { lat: number; lon: number }, to: { lat: number; lon: number }) {
  const R = Math.PI / 180;
  const dLat = (to.lat - from.lat) * R;
  const dLon = (to.lon - from.lon) * R;
  const h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(from.lat * R) * Math.cos(to.lat * R) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 12742 * Math.asin(Math.min(1, Math.sqrt(h)));
}
function f(n: number) {
  return Math.round(n * 10) / 10;
}
function esc(text: string) {
  return text.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

/** Five routes + five scenarios for a visitor location (exported for the tests and for the resting markup). */
export function buildPlace(data: GeoData, requested: VisitorPlace | null): McPlace {
  const { countries: COUNTRIES, regions: REGIONS, aliases: ALIASES } = data;
  const toCity = (cc: string, entry: GeoCity): McCity => ({ city: entry[0], code: entry[1], lat: entry[2], lon: entry[3], cc });
  const findCity = (cc: string, name: string | undefined): McCity | null => {
    const list = COUNTRIES[cc] ? COUNTRIES[cc]![2] : [];
    let folded = fold(name);
    if (ALIASES[folded]) folded = fold(ALIASES[folded]);
    if (!folded) return null;
    const hit = list.find((entry) => fold(entry[0]) === folded);
    return hit ? toCity(cc, hit) : null;
  };
  const codeOf = (name: string) => `${fold(name).replace(/ /g, '').toUpperCase()}XXX`.slice(0, 3);

  const geo = requested && COUNTRIES[requested.country] ? requested : FALLBACK_GEO;
  const cc = geo.country;
  const meta = COUNTRIES[cc]!;
  const hubEntry = meta[2].find((entry) => entry[0] === meta[1]) ?? meta[2][0]!;
  const hub = toCity(cc, hubEntry);
  let user: McCity | null = null;
  const known = findCity(cc, geo.city);
  if (known) user = known;
  else if (geo.city) {
    const at = geo.lat != null && geo.lon != null ? { lat: geo.lat, lon: geo.lon } : findCity(cc, (REGIONS[cc] || {})[String(geo.region).toUpperCase()]) || hub;
    user = { city: String(geo.city), code: codeOf(geo.city), lat: at.lat, lon: at.lon, cc };
  }
  const from = user || hub;
  const list: McCity[] = [];
  if (user && fold(user.city) !== fold(hub.city)) list.push(user);
  list.push(hub);
  const taken = (candidate: McCity) => list.some((picked) => fold(picked.city) === fold(candidate.city));
  const near = (pool: McCity[]) => pool.filter((candidate) => !taken(candidate)).sort((first, second) => km(from, first) - km(from, second));
  const own = near(meta[2].map((entry) => toCity(cc, entry)));
  while (list.length < 5 && own.length) list.push(own.shift()!);
  if (list.length < 5) {
    let all: McCity[] = [];
    Object.keys(COUNTRIES).forEach((code) => {
      if (code !== cc) COUNTRIES[code]![2].forEach((entry) => all.push(toCity(code, entry)));
    });
    all = near(all);
    while (list.length < 5 && all.length) list.push(all.shift()!);
  }
  /* latency: how far each city bends the path from the visitor to the hub (fibre: ~1 ms per 100 km) */
  const direct = km(from, hub);
  const rank = list.map((candidate, index) => ({ index, detour: km(from, candidate) + km(candidate, hub) - direct })).sort((first, second) => first.detour - second.detour || first.index - second.index);
  const base: number[] = [];
  rank.forEach((entry, position) => {
    base[entry.index] = position < 2 ? 0 : Math.min(40, 6 + position + Math.round(entry.detour / 100));
  });
  const A = rank[0]!.index;
  const B = rank[1]!.index;
  const scenarios: McScenario[] = [];
  for (let s = 0; s < 5; s++) {
    const row: McScenario = { bad: rint(BAD_LO, BAD_HI) };
    list.forEach((_city, index) => {
      const id = `r${index + 1}`;
      if (index === A) row[id] = s % 2 ? rint(7, 10) : rint(2, 3);
      else if (index === B) row[id] = s % 2 ? rint(2, 4) : rint(4, 5);
      else row[id] = (base[index] ?? 0) + rint(0, 2);
    });
    scenarios.push(row);
  }
  return { key: `${cc}|${fold(user ? user.city : '')}`, cc, hub, user, routes: list, scenarios };
}

export function initMulticonnection({ root, geo: GEO, strings: S, countryName }: McOptions): () => void {
  const q = <T extends Element>(selector: string) => root.querySelector<T>(selector)!;
  const disposers: (() => void)[] = [];
  const timers = new Set<number>();
  let destroyed = false;
  const later = (fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      if (!destroyed) fn();
    }, ms);
    timers.add(id);
    return id;
  };
  const listen = (target: EventTarget, type: string, handler: (event: any) => void) => {
    target.addEventListener(type, handler);
    disposers.push(() => target.removeEventListener(type, handler));
  };

  root.classList.add('is-js');
  const stage = q<HTMLElement>('[data-stage]');
  const svg = q<SVGSVGElement>('[data-wires]');
  const pkLayer = q<HTMLElement>('[data-packets]');
  const list = q<HTMLElement>('[data-routes]');
  const pcNode = q<HTMLElement>('[data-node="pc"]');
  const gameNode = q<HTMLElement>('[data-node="game"]');
  const directEl = q<HTMLElement>('[data-direct]');
  const directMs = q<HTMLElement>('[data-direct-ms]');
  const statusText = q<HTMLElement>('[data-status-text]');
  const liveEl = q<HTMLElement>('[data-live]');
  const bridge = q<HTMLElement>('[data-bridge]');
  const btn = q<HTMLElement>('[data-retest]');
  const btnLabel = btn.querySelector<HTMLElement>('[data-lbl]')!;
  const pauseBtn = q<HTMLElement>('[data-pause]');
  const clearBtn = q<HTMLButtonElement>('[data-clear]');
  const hintText = q<HTMLElement>('[data-hint]');
  const proofEl = q<HTMLElement>('[data-proof]');
  const proofSr = q<HTMLElement>('[data-proof-sr]');
  const proofGood = q<HTMLElement>('[data-proof-good]');
  const proofPct = q<HTMLElement>('[data-proof-pct]');
  const proofFill = root.querySelector<HTMLElement>('[data-proof-fill]');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isMobile = () => getComputedStyle(stage).getPropertyValue('--mc-mobile').trim() === '1';

  const { countries: COUNTRIES } = GEO;
  const ROUTES = [{ id: 'r1' }, { id: 'r2' }, { id: 'r3' }, { id: 'r4' }, { id: 'r5' }] as Route[];
  let SCENARIOS: McScenario[] = [];
  let BAD_MS = 208;
  let place: McPlace | null = null;

  function setProfile(profile: McPlace) {
    place = profile;
    ROUTES.forEach((route, index) => {
      const city = profile.routes[index]!;
      route.city = city.city;
      route.country = countryName(city.cc, COUNTRIES[city.cc]![0]);
      route.code = city.code;
    });
    SCENARIOS = profile.scenarios;
    BAD_MS = SCENARIOS[0]!.bad;
  }
  setProfile(buildPlace(GEO, forcedPlace() || FALLBACK_GEO));

  let scenario = 0;
  let winnerId: string | null = null;
  let focusId: string | null = null;
  let pinned: string | null = null;
  let testing = false;
  let visible = true;
  let paused = false;
  let boost = 1;
  const supportsMotion = typeof pkLayer.animate === 'function';
  const durFor = (lat: number) => 700 + lat * 14;
  const spY = (lat: number) => SP_H - 1.5 - (SP_H - 3) * Math.max(0, Math.min(1, (lat - SP_LO) / (SP_HI - SP_LO)));
  function drawSpark(route: Route) {
    const pts: string[] = [];
    const n = route.hist.length;
    let mean = 0;
    for (let i = 0; i < n; i++) mean += route.hist[i]!;
    mean /= n;
    const base = spY(mean);
    for (let i = 0; i < n; i++) pts.push(`${f(1.5 + ((SP_W - 3) * i) / (HIST_N - 1))},${f(Math.max(1.5, Math.min(SP_H - 1.5, base - (route.hist[i]! - mean) * SP_K)))}`);
    route.spLine.setAttribute('points', pts.join(' '));
    route.spFill.setAttribute('points', `1.5,${SP_H} ${pts.join(' ')} ${f(1.5 + ((SP_W - 3) * (n - 1)) / (HIST_N - 1))},${SP_H}`);
    const last = pts[n - 1]!.split(',');
    route.spDot.setAttribute('cx', last[0]!);
    route.spDot.setAttribute('cy', last[1]!);
  }
  function pushReading(route: Route) {
    route.hist.push(route.lat);
    if (route.hist.length > HIST_N) route.hist.shift();
    drawSpark(route);
  }
  function seedHist(route: Route, amp: number) {
    route.hist = [];
    const floor = route.lat <= LAT_MIN;
    for (let i = 0; i < HIST_N - 1; i++) route.hist.push(Math.max(LAT_MIN, route.lat + (floor ? 1 + Math.round(Math.random() * amp) : Math.round((Math.random() * 2 - 1) * amp))));
    route.hist.push(route.lat);
  }
  ROUTES.forEach((route) => {
    route.lat = SCENARIOS[0]![route.id]!;
    route.shown = route.lat;
    route.tw = 0;
    route.packets = [];
    route.set = null;
    route.geo = null;
    route.frames = null;
    route.el = list.querySelector<HTMLButtonElement>(`[data-route="${route.id}"]`)!;
    route.msEl = route.el.querySelector<HTMLElement>('[data-ms]')!;
    route.msWrap = route.el.querySelector<HTMLElement>('.mc-route-ms')!;
    route.deltaEl = route.el.querySelector<HTMLElement>('[data-delta]')!;
    route.spLine = route.el.querySelector<SVGPolylineElement>('.mc-route-spark polyline')!;
    route.spFill = route.el.querySelector<SVGPolygonElement>('.mc-route-spark .sp-fill')!;
    route.spDot = route.el.querySelector<SVGCircleElement>('.mc-route-spark .sp-dot')!;
    seedHist(route, 1);
    drawSpark(route);
  });
  const pcPlace = root.querySelector<HTMLElement>('[data-pc-place]');
  const gamePlace = root.querySelector<HTMLElement>('[data-game-place]');
  const resultBad = root.querySelector<HTMLElement>('[data-result-bad]');
  const proofBad = root.querySelector<HTMLElement>('[data-proof-bad]');
  const descEl = root.querySelector<HTMLElement>('[data-mc-desc]');
  function paintRow(route: Route) {
    route.el.querySelector('.mc-route-city')!.textContent = route.city;
    route.el.querySelector('.mc-route-country')!.textContent = route.country;
    route.el.querySelector('.mc-route-cc')!.textContent = route.code;
  }
  function paintPlace() {
    if (!place) return;
    if (pcPlace) pcPlace.textContent = place.user ? place.user.city : S.yourInternet;
    if (gamePlace) gamePlace.textContent = S.matchIn(place.hub.city);
    if (descEl)
      descEl.textContent = S.description(
        ROUTES.map((route) => route.city),
        place.hub.city
      );
  }
  const direct: Direct = { lat: BAD_MS, packets: [], set: null, geo: null, frames: null };
  function setBad(value: number, amp: number) {
    BAD_MS = Math.max(BAD_LO, Math.min(BAD_HI, value + (amp ? rint(-6, 6) : 0)));
    direct.lat = BAD_MS;
    if (proofBad) proofBad.textContent = String(BAD_MS);
    if (resultBad) resultBad.textContent = String(BAD_MS);
  }
  function relocate(requested: VisitorPlace) {
    const profile = buildPlace(GEO, requested);
    if (place && profile.key === place.key) return;
    setProfile(profile);
    publishPlace();
    scenario = 0;
    ROUTES.forEach((route) => {
      route.lat = SCENARIOS[0]![route.id]!;
      setShown(route, route.lat);
      paintRow(route);
      seedHist(route, 1);
      drawSpark(route);
    });
    paintPlace();
    setBad(BAD_MS, 0);
    const res = applyWinner(false);
    if (running) setRates();
    if (built) reaim(res);
    announce(res, '');
  }
  function askGeo() {
    if (forcedPlace()) return;
    askVisitorGeo().then((data) => {
      const visitor = normalizePlace(data);
      if (visitor && !destroyed) relocate(visitor);
    });
  }
  /* the page mirrors these routes elsewhere (window.__npMcPlace + "np:mc-place" event) */
  function publishPlace() {
    if (!place) return;
    try {
      // eslint-disable-next-line no-underscore-dangle -- the name is the design's page-level contract
      mcPlaceHost().__npMcPlace = place;
      window.dispatchEvent(new CustomEvent('np:mc-place', { detail: place }));
    } catch {
      /* a host without CustomEvent simply keeps the default set on the mirror */
    }
  }
  ROUTES.forEach((route) => {
    setShown(route, route.lat);
    paintRow(route);
  });
  paintPlace();
  setBad(BAD_MS, 0);
  askGeo();
  publishPlace();

  let built = false;
  const ports: { pc?: SVGCircleElement; game?: SVGCircleElement } = {};
  let measure: SVGPathElement | null = null;
  let mask: SVGMaskElement | null = null;
  let maskBg: SVGRectElement | null = null;
  let directPath: SVGPathElement | null = null;
  let directTail: SVGPathElement | null = null;
  const dports: SVGCircleElement[] = [];
  const trunks: SVGPathElement[] = [];
  const svgEl = <K extends keyof SVGElementTagNameMap>(tag: K) => document.createElementNS(NS, tag) as SVGElementTagNameMap[K];
  function makePackets(n: number, cls: string) {
    const set = document.createElement('div');
    set.className = `pk-set${cls ? ` ${cls}` : ''}`;
    const packets: Packet[] = [];
    for (let i = 0; i < n; i++) {
      const el = document.createElement('div');
      el.className = 'pk';
      set.appendChild(el);
      packets.push({ el, phase: (i + 0.15) / n, anim: null });
    }
    pkLayer.appendChild(set);
    return { set, packets };
  }
  function buildStage() {
    if (built || destroyed) return;
    built = true;
    const portLayer = svgEl('g');
    (['pc', 'game'] as const).forEach((key) => {
      const port = svgEl('circle');
      port.setAttribute('r', '4');
      port.setAttribute('class', `w-port${winnerId ? ' is-hot' : ''}`);
      portLayer.appendChild(port);
      ports[key] = port;
    });
    measure = svgEl('path');
    measure.setAttribute('fill', 'none');
    measure.setAttribute('stroke', 'none');
    svg.appendChild(measure);
    const defs = svgEl('defs');
    mask = svgEl('mask');
    mask.setAttribute('id', 'mc-knock');
    mask.setAttribute('maskUnits', 'userSpaceOnUse');
    mask.setAttribute('x', '0');
    mask.setAttribute('y', '0');
    mask.setAttribute('width', '100%');
    mask.setAttribute('height', '100%');
    maskBg = svgEl('rect');
    maskBg.setAttribute('width', '100%');
    maskBg.setAttribute('height', '100%');
    maskBg.setAttribute('fill', '#fff');
    mask.appendChild(maskBg);
    defs.appendChild(mask);
    svg.appendChild(defs);
    const directG = svgEl('g');
    directG.setAttribute('class', 'w-directwrap');
    const directInner = svgEl('g');
    directInner.setAttribute('mask', 'url(#mc-knock)');
    directPath = svgEl('path');
    directPath.setAttribute('class', 'w-direct');
    directTail = svgEl('path');
    directTail.setAttribute('class', 'w-direct');
    directInner.appendChild(directPath);
    directInner.appendChild(directTail);
    directG.appendChild(directInner);
    for (let dp = 0; dp < 4; dp++) {
      const dc = svgEl('circle');
      dc.setAttribute('r', dp < 2 ? '2.5' : '4');
      dc.setAttribute('class', `w-dport${dp < 2 ? '' : ' w-dport--end'}`);
      directG.appendChild(dc);
      dports.push(dc);
    }
    svg.appendChild(directG);
    const dpk = makePackets(2, 'is-direct');
    direct.set = dpk.set;
    direct.packets = dpk.packets;
    const trunkG = svgEl('g');
    trunkG.setAttribute('class', 'w-trunks');
    for (let tk = 0; tk < 2; tk++) {
      const tp = svgEl('path');
      tp.setAttribute('class', 'w-base w-trunk');
      trunkG.appendChild(tp);
      trunks.push(tp);
    }
    svg.appendChild(trunkG);
    ROUTES.forEach((route) => {
      const g = svgEl('g');
      g.setAttribute('data-wire', route.id);
      const base = svgEl('path');
      base.setAttribute('class', 'w-base');
      const glow = svgEl('path');
      glow.setAttribute('class', 'w-glow');
      const hotwrap = svgEl('g');
      hotwrap.setAttribute('class', 'w-hotwrap');
      const hot = svgEl('path');
      hot.setAttribute('class', 'w-hot');
      const sweep = svgEl('path');
      sweep.setAttribute('class', 'w-sweep');
      hotwrap.appendChild(hot);
      const pIn = svgEl('circle');
      const pOut = svgEl('circle');
      pIn.setAttribute('r', '2.75');
      pIn.setAttribute('class', 'w-rport');
      pOut.setAttribute('r', '2.75');
      pOut.setAttribute('class', 'w-rport');
      g.appendChild(base);
      g.appendChild(glow);
      g.appendChild(hotwrap);
      g.appendChild(sweep);
      g.appendChild(pIn);
      g.appendChild(pOut);
      svg.appendChild(g);
      const knock = svgEl('path');
      knock.setAttribute('class', 'w-knock');
      mask!.appendChild(knock);
      route.g = g;
      route.pathBase = base;
      route.pathGlow = glow;
      route.pathHot = hot;
      route.pathSweep = sweep;
      route.portIn = pIn;
      route.portOut = pOut;
      route.pathKnock = knock;
      const mp = makePackets(3, '');
      route.set = mp.set;
      route.packets = mp.packets;
      if (route.id === winnerId) {
        g.classList.add('is-winner');
        route.set.classList.add('is-winner');
      }
      if (route.id === focusId) {
        g.classList.add('is-focus');
        route.set.classList.add('is-focus');
      }
    });
    svg.appendChild(portLayer);
    layout(true);
  }
  function box(rect: DOMRect, s: DOMRect): Box {
    return { l: rect.left - s.left, t: rect.top - s.top, r: rect.right - s.left, b: rect.bottom - s.top, w: rect.width, h: rect.height, cx: rect.left - s.left + rect.width / 2, cy: rect.top - s.top + rect.height / 2 };
  }
  const rel = (el: Element, s: DOMRect) => box(el.getBoundingClientRect(), s);
  function fanA(x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return `C${f(x1 + dx * 0.42)},${f(y1 + dy * 0.34)} ${f(x2 - dx * 0.42)},${f(y2)} ${f(x2)},${f(y2)}`;
  }
  function fanB(x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return `C${f(x1 + dx * 0.42)},${f(y1)} ${f(x2 - dx * 0.42)},${f(y2 - dy * 0.34)} ${f(x2)},${f(y2)}`;
  }
  function stubIn(tx: number, cy: number, cl: number) {
    const run = Math.max(6, cl - tx);
    return `C${f(tx)},${f(cy - STUB_V * 0.45)} ${f(tx + run * 0.5)},${f(cy)} ${f(cl)},${f(cy)}`;
  }
  function stubOut(cr: number, cy: number, rx: number) {
    const run = Math.max(6, rx - cr);
    return `C${f(cr + run * 0.5)},${f(cy)} ${f(rx)},${f(cy + STUB_V * 0.55)} ${f(rx)},${f(cy + STUB_V)}`;
  }
  function sampleFrames(d: string, hideA: number, hideB: number, parks: number[]) {
    measure!.setAttribute('d', d);
    const len = measure!.getTotalLength();
    if (!len) return null;
    const n = Math.max(40, Math.min(160, Math.round(len / 7)));
    const pts: DOMPoint[] = [];
    for (let i = 0; i <= n; i++) pts.push(measure!.getPointAtLength((len * i) / n));
    const times = [0];
    for (let i = 1; i <= n; i++) {
      const L = (len * (i - 0.5)) / n;
      times.push(times[i - 1]! + (L > hideA && L < hideB ? 0.25 : 1));
    }
    const T = times[n]!;
    const frames: Keyframe[] = [];
    let prevA = 0;
    for (let i = 0; i <= n; i++) {
      const p0 = pts[Math.max(0, i - 1)]!;
      const p1 = pts[Math.min(n, i + 1)]!;
      let angle = (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI;
      if (i) {
        while (angle - prevA > 180) angle -= 360;
        while (angle - prevA < -180) angle += 360;
      }
      prevA = angle;
      const L = (len * i) / n;
      frames.push({ offset: times[i]! / T, transform: `translate(${f(pts[i]!.x)}px,${f(pts[i]!.y)}px) rotate(${f(angle)}deg)`, opacity: L > hideA + 3 && L < hideB - 3 ? 0 : 1 });
    }
    const parkIdx = parks.map((park) => Math.max(0, Math.min(n, Math.round((park / len) * n))));
    return { frames, parkIdx };
  }
  function setPath(mover: Mover) {
    const g = mover.geo;
    if (!g) return;
    measure!.setAttribute('d', g.head);
    const lenA = measure!.getTotalLength();
    const d = `${g.head} ${g.rest}`;
    measure!.setAttribute('d', d);
    const len = measure!.getTotalLength();
    const lenB = Math.max(0, len - lenA - g.w);
    if (!len) return;
    const parks = g.parks.map((park) => (park >= 0 ? park * lenA : lenA + g.w + -park * lenB));
    const res = sampleFrames(d, lenA, lenA + g.w, parks);
    if (!res) return;
    mover.framesHide = res.frames;
    mover.framesOpen = res.frames.map((frame) => ({ offset: frame.offset, transform: frame.transform }));
    mover.frames = framesFor(mover);
    mover.parkIdx = res.parkIdx;
    mover.packets.forEach((packet, i) => {
      if (packet.anim) {
        try {
          (packet.anim.effect as KeyframeEffect).setKeyframes(mover.frames!);
        } catch {
          const t = packet.anim.currentTime;
          packet.anim.cancel();
          packet.anim = null;
          makeAnim(packet, mover.frames!);
          if (packet.anim && t != null) (packet.anim as Animation).currentTime = t;
        }
      } else if (!running) {
        packet.el.style.transform = String(mover.frames![res.parkIdx[i % res.parkIdx.length]!]!.transform);
      }
    });
  }
  function framesFor(mover: Mover) {
    return mover.set && mover.set.classList.contains('is-winner') ? mover.framesOpen! : mover.framesHide!;
  }
  function refreshFrames(mover: Mover) {
    if (!mover.framesHide) return;
    const want = framesFor(mover);
    if (mover.frames === want) return;
    mover.frames = want;
    mover.packets.forEach((packet) => {
      if (packet.anim) {
        try {
          (packet.anim.effect as KeyframeEffect).setKeyframes(want);
        } catch {
          /* engines without setKeyframes keep the previous path until the next layout */
        }
      }
    });
  }
  function makeAnim(packet: Packet, frames: Keyframe[]) {
    packet.anim = packet.el.animate(frames, { duration: BASE_MS, iterations: Infinity, easing: 'linear', delay: -packet.phase * BASE_MS });
  }
  let lastKey = '';
  const pcIcon = pcNode.querySelector<HTMLElement>('.mc-node-icon')!;
  const gameIcon = gameNode.querySelector<HTMLElement>('.mc-node-icon')!;
  const geo: { mobile: boolean; pc: Box | null; game: Box | null; dir: Box | null; list: Box | null; rPc: number; rGame: number } = { mobile: false, pc: null, game: null, dir: null, list: null, rPc: 0, rGame: 0 };
  const axis: { y: number | null; raf: number; done: (() => void) | null } = { y: null, raf: 0, done: null };
  const endDy = { pc: 0, game: 0 };
  const wrapEl = q<HTMLElement>('.mc-routes-wrap');
  const snapped = { x: 0, y: 0 };
  const shiftXY = (rc: Box, dx: number, dy: number): Box => ({ l: rc.l + dx, r: rc.r + dx, t: rc.t + dy, b: rc.b + dy, w: rc.w, h: rc.h, cx: rc.cx + dx, cy: rc.cy + dy });
  const shiftBox = (rc: Box, dy: number): Box => ({ l: rc.l, r: rc.r, t: rc.t + dy, b: rc.b + dy, w: rc.w, h: rc.h, cx: rc.cx, cy: rc.cy + dy });
  const sideOff = (rc: Box, R: number) => Math.max(10, Math.min(22, rc.h / 2 - R - 8));
  function layout(force: boolean) {
    if (!built || destroyed) return;
    const s = svg.getBoundingClientRect();
    if (!s.width || !s.height) return;
    const dr = directEl.getBoundingClientRect();
    const wr = wrapEl.getBoundingClientRect();
    let rects = ROUTES.map((route) => rel(route.el, s));
    const mobileNow = isMobile();
    const dpr = window.devicePixelRatio || 1;
    const ux = wr.left - snapped.x;
    const uy = wr.top - snapped.y;
    const nx = Math.round(ux * dpr) / dpr - ux;
    const ny = Math.round(uy * dpr) / dpr - uy;
    const ddx = nx - snapped.x;
    const ddy = ny - snapped.y;
    const resnap = Math.abs(ddx) > 0.005 || Math.abs(ddy) > 0.005;
    const first = rects[0]!;
    const lastRect = rects[rects.length - 1]!;
    const key = [Math.round(s.width), Math.round(s.height), mobileNow ? 1 : 0, Math.round(first.t - snapped.y), Math.round(lastRect.b - snapped.y), Math.round(rects[1]!.t - snapped.y), Math.round(dr.top - snapped.y - s.top)].join('|');
    if (!force && !resnap && key === lastKey) return;
    lastKey = key;
    let pcR = rel(pcIcon, s);
    let gmR = rel(gameIcon, s);
    const rPc = parseFloat(getComputedStyle(pcIcon).borderTopLeftRadius) || 0;
    const rGame = parseFloat(getComputedStyle(gameIcon).borderTopLeftRadius) || 0;
    if (!mobileNow) {
      pcR = shiftBox(pcR, -endDy.pc);
      gmR = shiftBox(gmR, -endDy.game);
    }
    if (!pcR.w || !gmR.w || rects.some((rc) => !rc.w || !rc.h)) return;
    let dirB = box(dr, s);
    let listB: Box = { l: first.l, t: first.t, r: first.r, b: lastRect.b, w: first.w, h: lastRect.b - first.t, cx: first.cx, cy: (first.t + lastRect.b) / 2 };
    if (resnap) {
      rects = rects.map((rc) => shiftXY(rc, ddx, ddy));
      dirB = shiftXY(dirB, ddx, ddy);
      listB = shiftXY(listB, ddx, ddy);
    }
    ROUTES.forEach((route, i) => {
      route.rect = rects[i];
    });
    if (resnap) {
      snapped.x = nx;
      snapped.y = ny;
      wrapEl.style.transform = Math.abs(nx) < 0.005 && Math.abs(ny) < 0.005 ? '' : `translate(${Math.round(nx * 1000) / 1000}px,${Math.round(ny * 1000) / 1000}px)`;
    }
    geo.mobile = mobileNow;
    geo.pc = pcR;
    geo.game = gmR;
    geo.dir = dirB;
    geo.list = listB;
    geo.rPc = rPc;
    geo.rGame = rGame;
    svg.setAttribute('viewBox', `0 0 ${s.width} ${s.height}`);
    [mask!, maskBg!].forEach((el) => {
      el.setAttribute('x', String(-MASK_PAD));
      el.setAttribute('y', String(-MASK_PAD));
      el.setAttribute('width', String(s.width + 2 * MASK_PAD));
      el.setAttribute('height', String(s.height + 2 * MASK_PAD));
    });
    const mid = !!axis.raf;
    if (mid) {
      cancelAnimationFrame(axis.raf);
      axis.raf = 0;
    }
    axis.y = geo.mobile ? null : targetAxisY();
    drawWires();
    stage.classList.add('is-ready');
    if (motionArmed) scheduleMotion();
    if (mid) settleGlide();
  }
  function targetAxisY() {
    const winner = winnerId ? byId(winnerId) : null;
    return winner && winner.rect ? winner.rect.cy : geo.list!.cy;
  }
  function computeGeo(axisY: number | null): Geometry | null {
    if (!geo.pc || !geo.game || !geo.dir) return null;
    const { mobile } = geo;
    const dir = geo.dir;
    let pc: Box;
    let game: Box;
    let dPc = 0;
    let dGame = 0;
    if (mobile || axisY == null) {
      pc = geo.pc;
      game = geo.game;
    } else {
      dPc = axisY - geo.pc.cy;
      dGame = axisY - geo.game.cy;
      pc = shiftBox(geo.pc, dPc);
      game = shiftBox(geo.game, dGame);
    }
    type Point = { x: number; y: number };
    let a: Point;
    let b: Point;
    let ra: Point;
    let rb: Point;
    if (mobile) {
      a = { x: pc.cx, y: pc.b + 1 };
      b = { x: game.cx, y: game.t - 1 };
      ra = { x: pc.l - 1, y: pc.b - (geo.rPc + 5) };
      rb = { x: game.r + 1, y: game.t + (geo.rGame + 5) };
    } else {
      a = { x: pc.r + 1, y: pc.cy };
      b = { x: game.l - 1, y: game.cy };
      ra = { x: pc.r + 1, y: pc.cy + sideOff(pc, geo.rPc) };
      rb = { x: game.l - 1, y: game.cy + sideOff(game, geo.rGame) };
    }
    const G: Geometry = { mobile, dPc, dGame, a, b, ra, rb, routes: [], direct: null as unknown as Geometry['direct'], trunks: ['', ''] };
    if (mobile) {
      const firstCy = ROUTES[0]!.rect!.cy;
      const lastCy = ROUTES[ROUTES.length - 1]!.rect!.cy;
      G.trunks = [`M${f(a.x)},${f(a.y)} L${f(a.x)},${f(lastCy - STUB_V)}`, `M${f(b.x)},${f(firstCy + STUB_V)} L${f(b.x)},${f(b.y)}`];
    }
    ROUTES.forEach((route) => {
      const c = route.rect!;
      let segA;
      let segB;
      let base: string | null;
      let dHead;
      if (mobile) {
        const sIn = stubIn(a.x, c.cy, c.l);
        const sOut = stubOut(c.r, c.cy, b.x);
        segA = `L${f(a.x)},${f(c.cy - STUB_V)} ${sIn}`;
        segB = `${sOut} L${f(b.x)},${f(b.y)}`;
        dHead = `M${f(a.x)},${f(a.y)} ${segA}`;
        base = `M${f(a.x)},${f(c.cy - STUB_V)} ${sIn} M${f(c.r)},${f(c.cy)} ${sOut}`;
      } else {
        segA = fanA(a.x, a.y, c.l, c.cy);
        segB = fanB(c.r, c.cy, b.x, b.y);
        dHead = `M${f(a.x)},${f(a.y)} ${segA}`;
        base = null;
      }
      const full = `${dHead} M${f(c.r)},${f(c.cy)} ${segB}`;
      G.routes.push({ base: base || full, full, l: c.l, r: c.r, cy: c.cy, geo: { head: dHead, rest: `L${f(c.r)},${f(c.cy)} ${segB}`, w: c.w, parks: [0.45, 0.85, -0.6] } });
    });
    let dA;
    let dB;
    if (mobile) {
      dA = `L${f(ra.x)},${f(dir.cy - STUB_V)} ${stubIn(ra.x, dir.cy, dir.l)}`;
      dB = `${stubOut(dir.r, dir.cy, rb.x)} L${f(rb.x)},${f(rb.y)}`;
    } else {
      dA = fanA(ra.x, ra.y, dir.l, dir.cy);
      dB = fanB(dir.r, dir.cy, rb.x, rb.y);
    }
    const dirHead = `M${f(ra.x)},${f(ra.y)} ${dA}`;
    G.direct = { wire: `${dirHead} M${f(dir.r)},${f(dir.cy)} ${dB}`, head: dirHead, tail: `M${f(dir.r)},${f(dir.cy)} ${dB}`, l: dir.l, r: dir.r, cy: dir.cy, geo: { head: dirHead, rest: `L${f(dir.r)},${f(dir.cy)} ${dB}`, w: dir.w, parks: [0.55, -0.5] } };
    return G;
  }
  function fitDash(path: SVGPathElement, d: string) {
    path.setAttribute('d', d);
    const L = path.getTotalLength();
    const DASH = 6;
    if (!L) return;
    const n = Math.max(1, Math.round((L - DASH) / 13));
    path.setAttribute('stroke-dasharray', `${DASH} ${f(Math.max(3, (L - DASH) / n - DASH))}`);
  }
  function paintGeo(G: Geometry) {
    if (!G.mobile) {
      pcNode.style.setProperty('--mc-dy', `${f(G.dPc)}px`);
      gameNode.style.setProperty('--mc-dy', `${f(G.dGame)}px`);
      endDy.pc = G.dPc;
      endDy.game = G.dGame;
    }
    ports.pc!.setAttribute('cx', String(f(G.a.x)));
    ports.pc!.setAttribute('cy', String(f(G.a.y)));
    ports.game!.setAttribute('cx', String(f(G.b.x)));
    ports.game!.setAttribute('cy', String(f(G.b.y)));
    trunks[0]!.setAttribute('d', G.trunks[0]);
    trunks[1]!.setAttribute('d', G.trunks[1]);
    ROUTES.forEach((route, k) => {
      const wire = G.routes[k]!;
      route.pathBase!.setAttribute('d', wire.base);
      route.pathGlow!.setAttribute('d', wire.full);
      route.pathHot!.setAttribute('d', wire.full);
      route.pathSweep!.setAttribute('d', wire.full);
      route.pathKnock!.setAttribute('d', wire.full);
      route.portIn!.setAttribute('cx', String(f(wire.l)));
      route.portIn!.setAttribute('cy', String(f(wire.cy)));
      route.portOut!.setAttribute('cx', String(f(wire.r)));
      route.portOut!.setAttribute('cy', String(f(wire.cy)));
    });
    const d = G.direct;
    fitDash(directPath!, d.head);
    fitDash(directTail!, d.tail);
    const points: [number, number][] = [
      [d.l, d.cy],
      [d.r, d.cy],
      [G.ra.x, G.ra.y],
      [G.rb.x, G.rb.y],
    ];
    points.forEach(([x, y], index) => {
      dports[index]!.setAttribute('cx', String(f(x)));
      dports[index]!.setAttribute('cy', String(f(y)));
    });
  }
  function setMotionGeo(G: Geometry) {
    ROUTES.forEach((route, k) => {
      route.geo = G.routes[k]!.geo;
    });
    direct.geo = G.direct.geo;
  }
  function drawWires() {
    const G = computeGeo(axis.y);
    if (!G) return;
    paintGeo(G);
    setMotionGeo(G);
  }
  function springOut(p: number) {
    const c = 0.9;
    const qq = p - 1;
    return 1 + (c + 1) * qq * qq * qq + c * qq * qq;
  }
  function settleGlide() {
    if (axis.raf) {
      cancelAnimationFrame(axis.raf);
      axis.raf = 0;
    }
    const done = axis.done;
    axis.done = null;
    if (done) done();
  }
  function glideAxis(dur: number, done?: () => void) {
    settleGlide();
    if (!geo.pc || geo.mobile) {
      if (done) done();
      return;
    }
    const to = targetAxisY();
    const from = axis.y;
    if (from == null || reduced.matches || !dur || Math.abs(to - from) < 0.5) {
      axis.y = to;
      drawWires();
      if (done) done();
      return;
    }
    const t0 = performance.now();
    axis.done = done ?? null;
    const step = (now: number) => {
      if (destroyed) return;
      const p = Math.min(1, (now - t0) / dur);
      axis.y = from + (to - from) * springOut(p);
      drawWires();
      if (p < 1) {
        axis.raf = requestAnimationFrame(step);
        return;
      }
      axis.raf = 0;
      axis.y = to;
      drawWires();
      settleGlide();
    };
    axis.raf = requestAnimationFrame(step);
  }
  function reaim(res: WinnerResult, cb?: () => void) {
    const moves = !geo.mobile && res.changed && !!geo.pc;
    const ignite = () => {
      stage.classList.remove('is-aiming');
      if (!reduced.matches) {
        res.best.el.classList.remove('is-burst');
        void res.best.el.offsetWidth;
        res.best.el.classList.add('is-burst');
      }
      if (cb) cb();
    };
    if (!moves) {
      ignite();
      return;
    }
    stage.classList.add('is-aiming');
    const go = () => {
      if (motionArmed) {
        const G = computeGeo(targetAxisY());
        if (G) setMotionGeo(G);
        scheduleMotion(() => glideAxis(720, ignite));
      } else glideAxis(720, ignite);
    };
    if (reduced.matches) go();
    else later(go, 180);
  }
  let motionArmed = false;
  let motionQueue: Mover[] = [];
  let motionRaf = 0;
  let motionCbs: (() => void)[] = [];
  function scheduleMotion(cb?: () => void) {
    if (!built) {
      if (cb) cb();
      return;
    }
    motionQueue = [...ROUTES, direct];
    if (cb) motionCbs.push(cb);
    cancelAnimationFrame(motionRaf);
    motionRaf = requestAnimationFrame(motionStep);
  }
  function motionStep() {
    if (destroyed) return;
    const obj = motionQueue.shift();
    if (obj) setPath(obj);
    if (motionQueue.length) {
      motionRaf = requestAnimationFrame(motionStep);
      return;
    }
    motionRaf = 0;
    stage.classList.add('is-motion');
    if (reduced.matches || !supportsMotion) drawStatic();
    else syncRun();
    const cbs = motionCbs;
    motionCbs = [];
    cbs.forEach((cb) => cb());
  }
  function armMotion() {
    if (motionArmed) return;
    buildStage();
    motionArmed = true;
    if (stage.classList.contains('is-ready')) scheduleMotion();
  }
  const rateFor = (lat: number) => (BASE_MS / durFor(lat)) * boost;
  function ensureAnims(mover: Mover) {
    if (!mover.frames) return;
    mover.packets.forEach((packet) => {
      if (packet.anim) return;
      packet.el.style.transform = '';
      makeAnim(packet, mover.frames!);
    });
  }
  function setRates() {
    ROUTES.forEach((route) => {
      route.packets.forEach((packet) => {
        if (packet.anim) packet.anim.playbackRate = rateFor(route.lat);
      });
    });
    direct.packets.forEach((packet) => {
      if (packet.anim) packet.anim.playbackRate = rateFor(direct.lat) * 0.8;
    });
  }
  function eachAnim(fn: (anim: Animation) => void) {
    [...ROUTES, direct].forEach((mover) => {
      mover.packets.forEach((packet) => {
        if (packet.anim) fn(packet.anim);
      });
    });
  }
  let running = false;
  function start() {
    if (reduced.matches || paused || !supportsMotion || !built) return;
    running = true;
    root.classList.add('is-live');
    ROUTES.forEach(ensureAnims);
    ensureAnims(direct);
    setRates();
    eachAnim((anim) => {
      if (anim.playState !== 'running') anim.play();
    });
  }
  function stop() {
    running = false;
    root.classList.remove('is-live');
    eachAnim((anim) => anim.pause());
  }
  function drawStatic() {
    running = false;
    root.classList.remove('is-live');
    const park = (mover: Mover) => {
      if (!mover.frames || !mover.packets.length || !mover.parkIdx) return;
      mover.packets.forEach((packet, i) => {
        if (packet.anim) {
          packet.anim.cancel();
          packet.anim = null;
        }
        packet.el.style.transform = String(mover.frames![mover.parkIdx![i % mover.parkIdx!.length]!]!.transform);
      });
    };
    ROUTES.forEach(park);
    park(direct);
  }
  function bezier(p1x: number, p1y: number, p2x: number, p2y: number) {
    const A = (a1: number, a2: number) => 1 - 3 * a2 + 3 * a1;
    const B = (a1: number, a2: number) => 3 * a2 - 6 * a1;
    const C = (a1: number) => 3 * a1;
    const calc = (t: number, a1: number, a2: number) => ((A(a1, a2) * t + B(a1, a2)) * t + C(a1)) * t;
    const slope = (t: number, a1: number, a2: number) => 3 * A(a1, a2) * t * t + 2 * B(a1, a2) * t + C(a1);
    return (x: number) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let t = x;
      for (let i = 0; i < 6; i++) {
        const sl = slope(t, p1x, p2x);
        if (Math.abs(sl) < 1e-6) break;
        t -= (calc(t, p1x, p2x) - x) / sl;
      }
      return calc(t, p1y, p2y);
    };
  }
  const easeOut = bezier(0.16, 1, 0.3, 1);
  const pctFor = (lat: number) => Math.max(0, Math.min(99, Math.round(((BAD_MS - lat) / BAD_MS) * 100)));
  const proof = { lat: -1, bad: -1 };
  const resultGood = root.querySelector<HTMLElement>('[data-result-good]');
  const resultPct = root.querySelector<HTMLElement>('[data-result-pct]');
  function paintProof(lat: number, pct: number) {
    proofGood.textContent = String(lat);
    proofPct.textContent = String(pct);
    if (resultGood && resultPct) {
      resultGood.textContent = String(lat);
      resultPct.textContent = String(pct);
    }
  }
  function paintBar(lat: number) {
    if (proofFill) proofFill.style.setProperty('--mc-fill', `${Math.round((lat / BAD_MS) * 1000) / 10}%`);
  }
  function setProof(lat: number) {
    if (proof.lat === lat && proof.bad === BAD_MS) return;
    proof.lat = lat;
    proof.bad = BAD_MS;
    proofSr.textContent = S.proofSr(BAD_MS, lat, pctFor(lat));
    paintBar(lat);
  }
  const stMs = (route: Route) => `<span data-st-ms="${route.id}">${route.shown}</span>`;
  const stDelta = (route: Route, best: Route) => `<span data-st-delta="${route.id}">${Math.max(0, route.shown - best.shown)}</span>`;
  function setShown(route: Route, value: number) {
    route.shown = value;
    route.msEl.textContent = String(value);
  }
  function syncDerived() {
    const winner = winnerId ? byId(winnerId) : null;
    if (!winner) return;
    const W = winner.shown;
    ROUTES.forEach((route) => {
      route.deltaEl.textContent = route === winner ? S.activeNow : S.slowerBy(Math.max(0, route.shown - W));
    });
    directMs.textContent = String(BAD_MS - W);
    paintProof(W, pctFor(W));
    statusText.querySelectorAll<HTMLElement>('[data-st-ms],[data-st-delta]').forEach((el) => {
      const id = el.getAttribute('data-st-ms');
      if (id) {
        const route = byId(id);
        if (route) el.textContent = String(route.shown);
      } else {
        const route = byId(el.getAttribute('data-st-delta') || '');
        if (route) el.textContent = String(Math.max(0, route.shown - W));
      }
    });
  }
  function byId(id: string) {
    return ROUTES.find((route) => route.id === id) ?? null;
  }
  function rankOf(route: Route) {
    let n = 1;
    ROUTES.forEach((other, i) => {
      if (other !== route && (other.lat < route.lat || (other.lat === route.lat && i < ROUTES.indexOf(route)))) n++;
    });
    return n;
  }
  function flip(el: HTMLElement) {
    if (reduced.matches) return;
    el.classList.remove('is-flip');
    void el.offsetWidth;
    el.classList.add('is-flip');
  }
  function tickMs(route: Route, from: number, to: number) {
    if (route.tw) {
      cancelAnimationFrame(route.tw);
      route.tw = 0;
    }
    const badge0 = directMs.textContent;
    const land = () => {
      setShown(route, to);
      syncDerived();
      flip(route.msWrap);
      if (built && directMs.textContent !== badge0) flip(directMs.parentElement!);
    };
    if (reduced.matches || Math.abs(to - from) < 3) {
      land();
      return;
    }
    const t0 = performance.now();
    const dur = 560;
    const step = (now: number) => {
      if (destroyed) return;
      const p = Math.min(1, (now - t0) / dur);
      if (p < 1) {
        setShown(route, Math.round(from + (to - from) * easeOut(p)));
        syncDerived();
        route.tw = requestAnimationFrame(step);
        return;
      }
      route.tw = 0;
      land();
    };
    route.tw = requestAnimationFrame(step);
  }
  function labelFor(route: Route, best: Route) {
    return route.id === best.id ? S.routeAriaWinner(route.city, route.country, route.lat) : S.routeAriaSlower(route.city, route.country, route.lat, route.lat - best.lat);
  }
  function applyWinner(burst: boolean): WinnerResult {
    const best = ROUTES.slice().sort((first, second) => first.lat - second.lat)[0]!;
    const prev = winnerId ? byId(winnerId) : null;
    const changed = prev !== null && best.id !== prev.id;
    winnerId = best.id;
    ROUTES.forEach((route) => {
      const win = route.id === winnerId;
      route.el.classList.toggle('is-winner', win);
      if (route.g && route.set) {
        route.g.classList.toggle('is-winner', win);
        route.set.classList.toggle('is-winner', win);
        refreshFrames(route);
      }
      route.el.classList.remove('is-burst');
      route.el.setAttribute('aria-label', labelFor(route, best));
    });
    if (built) {
      ports.pc?.classList.add('is-hot');
      ports.game?.classList.add('is-hot');
    }
    if (burst && !reduced.matches) {
      void best.el.offsetWidth;
      best.el.classList.add('is-burst');
    }
    directEl.setAttribute('aria-label', S.directAria(BAD_MS - best.lat));
    const badge0 = directMs.textContent;
    syncDerived();
    if (built && directMs.textContent !== badge0) flip(directMs.parentElement!);
    setProof(best.lat);
    return { best, prev, changed };
  }
  const jitter = (value: number, amp: number) => Math.max(LAT_MIN, value + Math.round((Math.random() * 2 - 1) * Math.min(1, amp)));
  function applyScenario(sc: McScenario, amp: number, burst: boolean) {
    setBad(sc.bad, amp);
    ROUTES.forEach((route) => {
      route.lat = jitter(sc[route.id]!, amp);
      pushReading(route);
    });
    const res = applyWinner(false);
    ROUTES.forEach((route) => {
      if (route.shown !== route.lat) tickMs(route, route.shown, route.lat);
    });
    if (running) setRates();
    if (res.changed) reaim(res);
    else if (burst && !reduced.matches) {
      void res.best.el.offsetWidth;
      res.best.el.classList.add('is-burst');
    }
    return res;
  }
  let scrambleT = 0;
  function uncrown() {
    ROUTES.forEach((route) => {
      route.el.classList.remove('is-winner', 'is-burst');
      if (route.g && route.set) {
        route.g.classList.remove('is-winner');
        route.set.classList.remove('is-winner');
        refreshFrames(route);
      }
    });
  }
  function scramble(on: boolean) {
    window.clearInterval(scrambleT);
    scrambleT = 0;
    ROUTES.forEach((route) => {
      if (route.tw) {
        cancelAnimationFrame(route.tw);
        route.tw = 0;
      }
    });
    if (!on) return;
    if (reduced.matches) {
      ROUTES.forEach((route) => {
        route.msEl.textContent = '··';
      });
      return;
    }
    const tick = () =>
      ROUTES.forEach((route) => {
        route.msEl.textContent = String(2 + Math.floor(Math.random() * 40));
      });
    tick();
    scrambleT = window.setInterval(tick, 90);
  }
  function revealScenario(sc: McScenario, amp: number, done: () => void) {
    setBad(sc.bad, 0);
    ROUTES.forEach((route) => {
      route.lat = jitter(sc[route.id]!, amp);
      route.msEl.textContent = '··';
    });
    if (running) setRates();
    const finish = () => {
      ROUTES.forEach((route) => route.msWrap.classList.remove('is-set'));
      done();
    };
    if (reduced.matches) {
      ROUTES.forEach((route) => {
        setShown(route, route.lat);
        pushReading(route);
      });
      finish();
      return;
    }
    ROUTES.forEach((route, i) => {
      later(
        () => {
          setShown(route, route.lat);
          pushReading(route);
          route.msWrap.classList.add('is-set');
          flip(route.msWrap);
        },
        40 + i * 60
      );
    });
    later(finish, 40 + ROUTES.length * 60 + 160);
  }
  function statusFor(res: WinnerResult, ctx: string) {
    const b = res.best;
    if (res.changed && res.prev) return S.statusChanged(esc(res.prev.city), stMs(res.prev), esc(b.city), stMs(b));
    if (ctx === 'measured') return S.statusMeasured(esc(b.city), stMs(b));
    return S.statusLead(esc(b.city), stMs(b));
  }
  function announce(res: WinnerResult, ctx: string) {
    if (focusId) {
      describeFocus();
      return;
    }
    swapStatus(paused ? S.statusPaused(esc(res.best.city), stMs(res.best)) : statusFor(res, ctx), ctx === 'swap' && res.changed);
  }
  function swapStatus(html: string, fade: boolean) {
    if (statusText.innerHTML === html) return;
    statusText.classList.remove('is-swap');
    if (fade && !reduced.matches) {
      void statusText.offsetWidth;
      statusText.innerHTML = html;
      statusText.classList.add('is-swap');
      return;
    }
    statusText.innerHTML = html;
  }
  function describeFocus() {
    const route = focusId ? byId(focusId) : null;
    const best = winnerId ? byId(winnerId) : null;
    if (!route || !best) return;
    if (route.id === winnerId) {
      statusText.innerHTML = S.focusWinner(esc(route.city), stMs(route));
      return;
    }
    const next = rankOf(route) === 2;
    statusText.innerHTML = next ? S.focusNext(esc(route.city), stDelta(route, best), esc(best.city)) : S.focusReserve(esc(route.city), stDelta(route, best), esc(best.city));
  }
  function live(msg: string) {
    liveEl.textContent = '';
    liveEl.textContent = msg;
  }
  const engaged = () => !!(pinned || focusId || list.contains(document.activeElement));
  function retest(fromUser: boolean) {
    if (testing) return;
    testing = true;
    scenario = (scenario + 1) % SCENARIOS.length;
    stage.classList.add('is-testing');
    proofEl.classList.add('is-measuring');
    uncrown();
    scramble(true);
    ROUTES.forEach((route) => route.el.setAttribute('aria-label', S.routeAriaMeasuring(route.city, route.country)));
    directEl.setAttribute('aria-label', S.directAriaMeasuring);
    btn.classList.add('is-testing');
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-busy', 'true');
    btnLabel.textContent = S.measuringLabel;
    statusText.textContent = S.statusMeasuring;
    if (fromUser) live(S.liveMeasuring);
    boost = 2.4;
    if (running) setRates();
    later(
      () => {
        scramble(false);
        revealScenario(SCENARIOS[scenario]!, 1, () => {
          stage.classList.remove('is-testing');
          const res = applyWinner(false);
          proofEl.classList.remove('is-measuring');
          btn.classList.remove('is-testing');
          btn.removeAttribute('aria-disabled');
          btn.setAttribute('aria-busy', 'false');
          btnLabel.textContent = S.retestLabel;
          boost = 1;
          if (running) setRates();
          testing = false;
          reaim(res);
          announce(res, fromUser ? 'measured' : '');
          if (fromUser) {
            bridge.classList.add('is-hot');
            live(S.liveMeasured(res.best.city, res.best.lat, pctFor(res.best.lat), res.changed && res.prev ? res.prev.city : null));
          }
        });
      },
      reduced.matches ? 80 : 900
    );
  }
  let loopN = 0;
  function loop() {
    later(() => {
      if (built && visible && !document.hidden && !reduced.matches && !testing && !paused && !engaged()) {
        loopN++;
        if (loopN % 2 === 0) {
          scenario = (scenario + 1) % SCENARIOS.length;
          announce(applyScenario(SCENARIOS[scenario]!, 1, true), 'swap');
        } else announce(applyScenario(SCENARIOS[scenario]!, 3, false), '');
      }
      loop();
    }, 5200);
  }
  /* live readings: every half second the figures move within their bands without changing who leads */
  function livePing() {
    later(() => {
      if (built && visible && !document.hidden && !reduced.matches && !testing && !paused && winnerId) {
        const winner = byId(winnerId)!;
        const sc = SCENARIOS[scenario]!;
        winner.lat = rint(2, 5);
        ROUTES.forEach((route) => {
          if (route !== winner) route.lat = Math.max(winner.lat + 1, jitter(sc[route.id]!, 1));
          if (route.tw) {
            cancelAnimationFrame(route.tw);
            route.tw = 0;
          }
          setShown(route, route.lat);
          pushReading(route);
        });
        setBad(rint(BAD_LO, BAD_HI), 0);
        syncDerived();
        setProof(winner.lat);
      }
      livePing();
    }, LIVE_MS);
  }
  livePing();
  let hintGone = false;
  function dismissHint() {
    if (hintGone) return;
    hintGone = true;
    if (reduced.matches) {
      hintText.hidden = true;
      return;
    }
    hintText.classList.add('is-gone');
    later(() => {
      hintText.hidden = true;
    }, 450);
  }
  function setFocus(id: string | null, sticky?: boolean) {
    if (id) dismissHint();
    focusId = id;
    stage.classList.toggle('has-focus', !!id);
    ROUTES.forEach((route) => {
      const on = route.id === id;
      route.el.classList.toggle('is-focus', on);
      if (route.g && route.set) {
        route.g.classList.toggle('is-focus', on);
        route.set.classList.toggle('is-focus', on);
      }
      if (sticky !== undefined) route.el.setAttribute('aria-pressed', on && sticky ? 'true' : 'false');
    });
    if (id) describeFocus();
    else if (winnerId) announce({ best: byId(winnerId)!, prev: null, changed: false }, '');
  }
  function setPin(id: string | null) {
    pinned = id;
    ROUTES.forEach((route) => route.el.classList.toggle('is-pinned', route.id === id));
    root.classList.toggle('has-pin', !!id);
    if (id) {
      hintGone = true;
      hintText.classList.add('is-gone');
      hintText.hidden = true;
    }
    clearBtn.hidden = !id;
  }
  function clearPin(returnFocusTo?: HTMLElement | null) {
    const had = !!(pinned || focusId);
    setPin(null);
    setFocus(null, false);
    if (had) live(S.liveCleared);
    if (returnFocusTo) returnFocusTo.focus({ preventScroll: true });
  }
  const routeOf = (target: EventTarget | null) => (target instanceof Element ? target.closest<HTMLElement>('[data-route]') : null);
  listen(list, 'pointerover', (event: PointerEvent) => {
    const button = routeOf(event.target);
    if (!button || pinned || event.pointerType === 'touch') return;
    setFocus(button.getAttribute('data-route'));
  });
  listen(list, 'pointerout', (event: PointerEvent) => {
    const button = routeOf(event.target);
    if (!button || pinned || event.pointerType === 'touch') return;
    if (event.relatedTarget instanceof Node && button.contains(event.relatedTarget)) return;
    setFocus(null);
  });
  listen(list, 'click', (event: MouseEvent) => {
    const button = routeOf(event.target);
    if (!button) return;
    const id = button.getAttribute('data-route')!;
    if (pinned === id) {
      clearPin();
      return;
    }
    setPin(id);
    setFocus(id, true);
    if (event.detail === 0) clearBtn.focus({ preventScroll: true });
    const route = byId(id)!;
    const winner = byId(winnerId!)!;
    live(id === winnerId ? S.livePinnedWinner(route.city, route.lat) : S.livePinnedSlower(route.city, route.lat, route.lat - winner.lat, winner.city));
  });
  listen(list, 'focusin', (event: FocusEvent) => {
    const button = routeOf(event.target);
    if (!button || pinned) return;
    setFocus(button.getAttribute('data-route'));
  });
  listen(list, 'focusout', (event: FocusEvent) => {
    if (pinned) return;
    if (!(event.relatedTarget instanceof Node) || !list.contains(event.relatedTarget)) setFocus(null);
  });
  listen(stage, 'click', (event: MouseEvent) => {
    if (pinned && !routeOf(event.target)) clearPin();
  });
  listen(clearBtn, 'click', (event: MouseEvent) => {
    const to = pinned && event.detail === 0 ? byId(pinned)!.el : null;
    clearPin(to);
  });
  listen(root, 'keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && (pinned || focusId)) clearPin();
  });
  listen(btn, 'click', () => {
    if (btn.getAttribute('aria-disabled') === 'true') return;
    retest(true);
  });
  listen(pauseBtn, 'click', () => {
    paused = !paused;
    root.classList.toggle('is-paused', paused);
    pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    if (paused) stop();
    else syncRun();
    if (winnerId) announce({ best: byId(winnerId)!, prev: null, changed: false }, '');
    live(paused ? S.livePaused : S.liveResumed);
  });
  function syncRun() {
    const on = visible && !document.hidden;
    root.classList.toggle('is-idle', !on);
    if (on) {
      armMotion();
      start();
    } else stop();
  }
  if ('IntersectionObserver' in window) {
    const near = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0]?.isIntersecting) {
          buildStage();
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px' }
    );
    near.observe(stage);
    const seen = new IntersectionObserver(
      (entries) => {
        visible = !!entries[0]?.isIntersecting;
        syncRun();
      },
      { threshold: 0.05 }
    );
    seen.observe(stage);
    disposers.push(() => {
      near.disconnect();
      seen.disconnect();
    });
    later(() => {
      if (built || document.hidden) return;
      const r = stage.getBoundingClientRect();
      if (r.bottom > -320 && r.top < window.innerHeight + 320) buildStage();
    }, 1500);
  } else {
    visible = true;
    armMotion();
  }
  listen(document, 'visibilitychange', syncRun);
  let resizeRaf = 0;
  const scheduleLayout = () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => layout(false));
  };
  if ('ResizeObserver' in window) {
    const resize = new ResizeObserver(scheduleLayout);
    resize.observe(stage);
    disposers.push(() => resize.disconnect());
  } else listen(window, 'resize', scheduleLayout);
  document.fonts?.ready.then(() => {
    if (!destroyed) scheduleLayout();
  });
  const onMotionPref = () => {
    if (reduced.matches) {
      stop();
      drawStatic();
    } else syncRun();
  };
  if (reduced.addEventListener) {
    reduced.addEventListener('change', onMotionPref);
    disposers.push(() => reduced.removeEventListener('change', onMotionPref));
  }
  announce(applyWinner(false), '');
  loop();

  return () => {
    destroyed = true;
    timers.forEach((id) => window.clearTimeout(id));
    timers.clear();
    window.clearInterval(scrambleT);
    cancelAnimationFrame(resizeRaf);
    cancelAnimationFrame(motionRaf);
    cancelAnimationFrame(axis.raf);
    ROUTES.forEach((route) => {
      if (route.tw) cancelAnimationFrame(route.tw);
    });
    eachAnim((anim) => anim.cancel());
    disposers.forEach((dispose) => dispose());
    // the wires and packets this module drew are not React's: leave the containers as React rendered them
    svg.replaceChildren();
    pkLayer.replaceChildren();
    root.classList.remove('is-js', 'is-live', 'is-idle', 'is-paused', 'has-pin');
  };
}
